/**
 * Returns a function, that, when invoked, will only be triggered at most once during
 * a given window of time.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Time window (in milliseconds).
 * @returns {Function}
 */
function throttle(fn, wait = 300) {
  let throttleTimeoutId = -1;
  let tick = false;

  return () => {
    clearTimeout(throttleTimeoutId);
    throttleTimeoutId = setTimeout(fn, wait);

    if (!tick) {
      fn.call();
      tick = true;
      setTimeout(() => {
        tick = false;
      }, wait);
    }
  };
}

if (!customElements.get('sticky-atc')) {
  customElements.whenDefined('product-form').then(() => {
    class StickyAtc extends customElements.get('product-form') {
      constructor() {
        super();

        this.section = this.closest('.section-main-product');
        this.mainProductForm = this.section.querySelector('.js-product');
        this.buyButtons = this.section.querySelector('buy-buttons');
        this.imageContainer = this.querySelector('.sticky-atc__image');
        this.variantContainer = this.querySelector('.sticky-atc__details__variant');
        this.variantTitle = this.querySelector('.sticky-atc__details__variant__title');
        this.variantTitle?.toggleAttribute('hidden', !this.section.querySelector('variant-picker'));

        // Inline option chips. Present only when the product has a single option,
        // so a chip resolves to exactly one variant (see snippets/sticky-atc.liquid).
        this.optionsContainer = this.querySelector('.sticky-atc__options');
        this.optionChips = Array.from(this.querySelectorAll('.sticky-atc__option'));
        this.requireSelection = this.hasAttribute('data-require-selection');

        this.priceEl = this.querySelector('.sticky-atc__price');
        this.priceWasEl = this.querySelector('.sticky-atc__price-was');

        this.throttledOnScroll = throttle(StickyAtc.handleScroll.bind(this));
        window.addEventListener('scroll', this.throttledOnScroll);

        this.section.addEventListener('on:variant:change', this.onVariantChange.bind(this));
        this.section.addEventListener('on:media-gallery:change', this.updateImage.bind(this));

        if (this.optionsContainer) {
          this.optionsContainer.addEventListener('click', this.onOptionClick.bind(this));
        }

        this.updateImage();
        this.syncOptions();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.throttledOnScroll);
      }

      /**
       * Handles submission of the product form.
       * @param {object} evt - Event object.
       */
      async handleSubmit(evt) {
        evt.preventDefault();

        // PDP v2 (spec 019 J2): when the main form opts in to inline validation,
        // both "nothing chosen" paths go through its validate(), which writes the
        // inline alert and focuses the first available size. Without the
        // attribute the branches below run exactly as before.
        const inlineValidation = this.mainProductForm.hasAttribute('data-inline-validation');
        const variantId = this.section.querySelector('product-form buy-buttons input[name="id"]').value;
        if (!variantId && inlineValidation) {
          this.mainProductForm.validate();
          theme.scrollToRevealElement(this.mainProductForm.querySelector('variant-picker') || this.mainProductForm);
          return;
        }
        if (!variantId) {
          const target = this.section.querySelector('#product-info');
          if (target && target.offsetParent) {
            evt.preventDefault();
            theme.scrollToRevealElement(target);
          }
          return;
        }

        // Validate main form
        const customFormValid = this.mainProductForm.validate();
        if (!customFormValid || !this.mainProductForm.form.reportValidity()) {
          evt.preventDefault();
          if (!customFormValid && inlineValidation) {
            theme.scrollToRevealElement(this.mainProductForm.querySelector('variant-picker') || this.mainProductForm);
          } else if (!customFormValid) {
            theme.scrollToRevealElement(this.mainProductForm);
          } else {
            const input = Array.from(this.mainProductForm.form.elements).find(
              (el) => !el.checkValidity()
            );
            setTimeout(() => theme.scrollToRevealElement(input), 100);
          }
          return;
        }

        // Copy data
        this.form.querySelectorAll('[data-copied]').forEach((el) => el.remove());
        const formData = new FormData(this.mainProductForm.form);
        for (const p of formData) {
          if (!this.form.querySelector(`[name="${p[0]}"]`)) {
            const input = document.createElement('input');
            input.name = p[0];
            input.value = p[1];
            input.hidden = true;
            input.setAttribute('data-copied', '');
            this.form.append(input);
          }
        }
        super.handleSubmit(evt);
      }

      /**
       * Determine visibility after scroll.
       */
      static handleScroll() {
        const topOffset = document.querySelector('.pageheader--sticky') ? document.querySelector('.section-header').offsetHeight : 0;
        this.classList.toggle(
          'sticky-atc--out',
          this.buyButtons.getBoundingClientRect().bottom > topOffset
        );

        document.body.classList.toggle(
          'scrolled-to-bottom',
          window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
        );
      }

      /**
       * Finds the buy-box option selector that the sticky chips mirror.
       * Deliberately not cached: the picker markup can be replaced by a section
       * re-render, and re-querying on a variant change costs nothing.
       * @returns {?Element}
       */
      getOptionSelector() {
        if (!this.optionsContainer) return null;

        const picker = this.section.querySelector('variant-picker');
        if (!picker) return null;

        const name = this.optionsContainer.dataset.option;
        return Array.from(picker.querySelectorAll('.option-selector'))
          .find((selector) => selector.dataset.option === name) || null;
      }

      /**
       * Handles a click on an option chip by driving the buy-box picker rather
       * than holding its own selection state. Everything downstream — price,
       * add-to-cart label, availability, this bar — then updates through the
       * existing `on:variant:change` flow, so there is one source of truth.
       * @param {Event} evt - Click event.
       */
      onOptionClick(evt) {
        const chip = evt.target.closest('.sticky-atc__option');
        if (!chip) return;

        const selector = this.getOptionSelector();
        if (!selector) return;

        const input = Array.from(selector.querySelectorAll('.js-option'))
          .find((el) => el.value === chip.dataset.value);
        if (!input || input.checked) return;

        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      /**
       * Mirrors the buy-box picker state onto the option chips.
       */
      syncOptions() {
        if (!this.optionChips.length) return;

        const selector = this.getOptionSelector();
        if (!selector) return;

        const picker = selector.closest('variant-picker');
        // When dynamic availability is off the picker never marks its inputs, so
        // the server-rendered availability on the chips is the only truth we have.
        const syncAvailability = !picker || picker.dataset.showAvailability !== 'false';
        const inputs = Array.from(selector.querySelectorAll('.js-option'));

        let selectedChip = null;

        this.optionChips.forEach((chip) => {
          const input = inputs.find((el) => el.value === chip.dataset.value);
          if (!input) return;

          chip.classList.toggle('is-selected', input.checked);
          chip.setAttribute('aria-pressed', input.checked ? 'true' : 'false');
          if (input.checked) selectedChip = chip;

          if (!syncAvailability) return;

          chip.classList.toggle('is-unavailable', input.classList.contains('is-unavailable'));
          chip.classList.toggle('is-nonexistent', input.classList.contains('is-nonexistent'));

          const title = input.nextElementSibling?.getAttribute('title');
          if (title) {
            chip.setAttribute('title', title);
          } else {
            chip.removeAttribute('title');
          }
        });

        this.revealSelectedOption(selectedChip);
      }

      /**
       * On narrow screens the full size run does not fit, so the chip row scrolls.
       * When the selection changes — including from the buy box, which the shopper
       * cannot see while the bar is up — bring the selected chip into view so the
       * bar never appears to have ignored them.
       * @param {?Element} chip - Currently selected chip, if any.
       */
      revealSelectedOption(chip) {
        const value = chip ? chip.dataset.value : null;
        if (value === this.revealedValue) return;
        this.revealedValue = value;

        const track = this.optionsContainer;
        if (!chip || !track || track.scrollWidth <= track.clientWidth) return;

        // Scroll the track itself rather than using scrollIntoView, which would
        // also scroll the page and yank the shopper away from what they were reading.
        const target = chip.offsetLeft - (track.clientWidth - chip.offsetWidth) / 2;
        const left = Math.max(0, Math.min(target, track.scrollWidth - track.clientWidth));
        track.scrollTo({ left, behavior: 'smooth' });
      }

      /**
       * Handle a change in variant on the page.
       * @param {Event} evt - Variant change event dispatched by variant-picker.
       */
      onVariantChange(evt) {
        const idInput = this.form?.querySelector('[name="id"]');

        if (evt.detail.variant) {
          this.variantTitle.textContent = evt.detail.variant.title;
          this.variantContainer.hidden = false;
          if (idInput) idInput.value = evt.detail.variant.id;
        } else {
          this.variantContainer.hidden = true;
          this.variantTitle.textContent = '';
          if (idInput) idInput.value = '';
        }

        this.updateImage();
        this.syncOptions();
        this.updatePrice(evt.detail.variant);
        this.updateAddToCartButton(evt.detail.variant);
        this.setErrorMsgState();
      }

      /**
       * Updates the image.
       */
      updateImage() {
        const mainImage = this.section.querySelector('.media-gallery .main-image .slider__item.is-active .product-media img');

        if (mainImage) {
          this.imageContainer.innerHTML = mainImage.outerHTML;
        } else {
          this.imageContainer.textContent = '';
        }
      }

      /**
       * Reads the money-formatted prices the variant picker already renders, so
       * the bar can never format currency differently to the buy box.
       * @returns {?object}
       */
      getFormattedPrices() {
        if (this.formattedPrices !== undefined) return this.formattedPrices;

        this.formattedPrices = null;
        const dataEl = this.section.querySelector('variant-picker [type="application/json"]');

        if (dataEl) {
          try {
            this.formattedPrices = JSON.parse(dataEl.textContent).formatted;
          } catch (e) {
            this.formattedPrices = null;
          }
        }

        return this.formattedPrices;
      }

      /**
       * Updates the price shown in the bar.
       * @param {object} variant - Current variant object.
       */
      updatePrice(variant) {
        if (!this.priceEl) return;

        const formatted = this.getFormattedPrices();
        const entry = variant && formatted ? formatted[variant.id] : null;

        if (entry) {
          this.priceEl.innerHTML = entry.price;
        } else {
          this.priceEl.innerHTML = this.priceEl.dataset.defaultPrice || '';
        }

        if (this.priceWasEl) {
          const compareAt = entry ? entry.compareAtPrice : null;
          this.priceWasEl.innerHTML = compareAt || '';
          this.priceWasEl.hidden = !compareAt;
        }
      }

      /**
       * Updates the "Add to Cart" button label and status.
       * @param {object} variant - Current variant object.
       */
      updateAddToCartButton(variant) {
        if (!this.submitBtn) return;

        if (!variant) {
          // With chips in the bar the shopper can resolve a variant from here, so
          // the button keeps its real label and is disabled until they do.
          this.submitBtn.textContent = this.submitBtn.dataset.noVariantText
            || this.submitBtn.dataset.unavailableText;
          this.submitBtn.disabled = this.requireSelection;
          return;
        }

        this.submitBtn.textContent = variant.available
          ? this.submitBtn.dataset.addToCartText
          : theme.strings.noStock;
        this.submitBtn.disabled = !variant.available;
      }
    }

    customElements.define('sticky-atc', StickyAtc);
  });
}
